import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "../../layout/Navbar";
import Footer from "../../layout/Footer";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Questions = () => {
    const [categories, setCategories] = useState([]);
    const [examTypes, setExamTypes] = useState([]);
    const [selectedExamType, setSelectedExamType] = useState(null);
    const [yearsData, setYearsData] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState("examTypes"); // "examTypes", "years", "downloads"
    const [selectedYear, setSelectedYear] = useState(null);
    const [questions, setQuestions] = useState([]);
    const navigate = useNavigate();
    const params = useParams();

    // Define main exam types with icons and colors
    const examTypeConfig = {
        "BECE": {
            name: "BECE",
            fullName: "Basic Education Certificate Examination",
            icon: "bi-mortarboard",
            color: "primary",
            description: "Junior High School final exams",
            keywords: ["bece", "basic education", "junior high", "jhs"]
        },
        "WASSCE": {
            name: "WASSCE",
            fullName: "West African Senior School Certificate Examination",
            icon: "bi-book",
            color: "success",
            description: "Senior High School final exams",
            keywords: ["wassce", "w.a.s.s.c.e", "senior high", "shs", "secondary"]
        },
        "CTVET": {
            name: "CTVET",
            fullName: "Commission for Technical and Vocational Education and Training",
            icon: "bi-gear",
            color: "warning",
            description: "Technical and vocational exams",
            keywords: ["ctvet", "technical", "vocational", "naabtech", "nabtech", "business", "nbt", "craft"]
        },
        "University": {
            name: "University",
            fullName: "University Past Questions",
            icon: "bi-building",
            color: "info",
            description: "Undergraduate level exams",
            keywords: ["university", "degree", "undergraduate", "college", "polytechnic"]
        }
    };

    // Fetch categories on component mount
    useEffect(() => {
        fetchCategories();
    }, []);

    // Handle URL parameters
    useEffect(() => {
        if (params.examType) {
            const examType = Object.keys(examTypeConfig).find(type => 
                type.toLowerCase() === params.examType.toLowerCase()
            );
            if (examType) {
                handleExamTypeClick(examType);
            }
        }
        if (params.year) {
            setSelectedYear(params.year);
            fetchQuestionsForYear(params.year);
        }
    }, [params, categories]);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                "https://pastq.eddcodesgh.com/qApi/client/questions/_categories.php",
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                    },
                    withCredentials: true,
                }
            );

            if (response.data.status === "success") {
                const categoriesData = response.data.records || [];
                console.log("Raw categories data:", categoriesData);
                setCategories(categoriesData);
                processExamTypes(categoriesData);
            } else {
                toast.error("Failed to load exam materials");
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            toast.error("Network error loading exam materials");
        } finally {
            setLoading(false);
        }
    };

    const categorizeExamType = (category) => {
        const title = (category.title || '').toLowerCase();
        const description = (category.description || '').toLowerCase();
        const examTypeFromApi = (category.exam_type || category.type || '').toLowerCase();
        
        // First, check if API provides exam type
        if (examTypeFromApi) {
            for (const [type, config] of Object.entries(examTypeConfig)) {
                if (config.keywords.some(keyword => examTypeFromApi.includes(keyword.toLowerCase()))) {
                    return type;
                }
            }
        }
        
        // Check title for keywords (with priority for specific types)
        // Check for CTVET first (to avoid mis-categorizing CTVET as BECE)
        if (examTypeConfig.CTVET.keywords.some(keyword => 
            title.includes(keyword) || description.includes(keyword)
        )) {
            return "CTVET";
        }
        
        // Check for BECE (but exclude if it contains CTVET-related terms)
        if (examTypeConfig.BECE.keywords.some(keyword => 
            (title.includes(keyword) || description.includes(keyword)) &&
            !title.includes('ctvet') && !title.includes('technical') &&
            !description.includes('ctvet') && !description.includes('technical')
        )) {
            return "BECE";
        }
        
        // Check for WASSCE
        if (examTypeConfig.WASSCE.keywords.some(keyword => 
            title.includes(keyword) || description.includes(keyword)
        )) {
            return "WASSCE";
        }
        
        // Check for University
        if (examTypeConfig.University.keywords.some(keyword => 
            title.includes(keyword) || description.includes(keyword)
        )) {
            return "University";
        }
        
        return "Other";
    };

    const processExamTypes = (categoriesData) => {
        // Group categories by exam type
        const groupedByType = {};
        
        // Initialize all exam types
        Object.keys(examTypeConfig).forEach(type => {
            groupedByType[type] = {
                categories: [],
                years: new Set()
            };
        });
        groupedByType["Other"] = {
            categories: [],
            years: new Set()
        };
        
        categoriesData.forEach(category => {
            const examType = categorizeExamType(category);
            
            groupedByType[examType].categories.push(category);
            
            // Add years from this category
            if (category.years && Array.isArray(category.years)) {
                category.years.forEach(year => {
                    if (year && !isNaN(year)) {
                        groupedByType[examType].years.add(parseInt(year));
                    }
                });
            }
        });

        console.log("Grouped by type:", groupedByType);

        // Convert to array format for display
        const examTypesArray = Object.keys(examTypeConfig).map(type => {
            const data = groupedByType[type] || { categories: [], years: new Set() };
            const yearsArray = Array.from(data.years).sort((a, b) => b - a);
            
            return {
                id: type,
                name: examTypeConfig[type].name,
                fullName: examTypeConfig[type].fullName,
                icon: examTypeConfig[type].icon,
                color: examTypeConfig[type].color,
                description: examTypeConfig[type].description,
                categoryCount: data.categories.length,
                years: yearsArray,
                yearCount: yearsArray.length,
                categories: data.categories,
                hasContent: data.categories.length > 0
            };
        }).filter(type => type.hasContent); // Only show types with content

        setExamTypes(examTypesArray);
        setYearsData(groupedByType);
    };

    const handleExamTypeClick = (examType) => {
        const typeData = examTypes.find(t => t.id === examType);
        if (!typeData || typeData.categoryCount === 0) {
            toast.info(`No content available for ${examType} yet`);
            return;
        }

        setSelectedExamType(typeData);
        setSelectedYear(null);
        setQuestions([]);
        setActiveTab("years");
        
        // Update URL
        navigate(`/questions/${examType.toLowerCase()}`);
    };

    const handleYearClick = (year) => {
        setSelectedYear(year);
        fetchQuestionsForYear(year);
        setActiveTab("downloads");
        
        // Update URL
        navigate(`/questions/${selectedExamType.id.toLowerCase()}/${year}`);
    };

    const fetchQuestionsForYear = async (year) => {
        if (!selectedExamType) return;

        setLoading(true);
        try {
            // Get all categories for this exam type
            const categoryIds = selectedExamType.categories
                .map(cat => cat.id)
                .filter(id => id)
                .join(',');
            
            if (!categoryIds) {
                toast.error("No categories found for this exam type");
                setQuestions([]);
                return;
            }
            
            const response = await axios.get(
                `https://pastq.eddcodesgh.com/qApi/admin/questions/getquestions.php?category_id=${categoryIds}&year=${year}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                    },
                    withCredentials: true,
                }
            );

            if (response.data.status === "success") {
                setQuestions(response.data.records || []);
                if (response.data.records.length === 0) {
                    toast.info(`No questions found for ${selectedExamType.name} ${year}`);
                }
            } else {
                toast.error("No questions found for this year");
                setQuestions([]);
            }
        } catch (error) {
            console.error("Error fetching questions:", error);
            toast.error("Failed to load questions");
            setQuestions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = (question, type = "question") => {
        if (type === "question" && question.question_file) {
            const fileUrl = `https://pastq.eddcodesgh.com/qApi/uploads/questions/${question.question_file}`;
            window.open(fileUrl, '_blank');
            toast.success("Downloading question paper...");
        } else if (type === "answer" && question.answer_file) {
            const fileUrl = `https://pastq.eddcodesgh.com/qApi/uploads/questions/${question.answer_file}`;
            window.open(fileUrl, '_blank');
            toast.success("Downloading answer sheet...");
        } else {
            toast.error("File not available for download");
        }
    };

    const goBackToExamTypes = () => {
        setSelectedExamType(null);
        setSelectedYear(null);
        setQuestions([]);
        setActiveTab("examTypes");
        navigate("/questions");
    };

    const goBackToYears = () => {
        setSelectedYear(null);
        setQuestions([]);
        setActiveTab("years");
        navigate(`/questions/${selectedExamType.id.toLowerCase()}`);
    };

    // Breadcrumb navigation
    const Breadcrumb = () => (
        <nav aria-label="breadcrumb">
            <ol className="breadcrumb bg-light p-3 rounded">
                <li className="breadcrumb-item">
                    <button 
                        className="btn btn-link text-decoration-none p-0"
                        onClick={goBackToExamTypes}
                    >
                        <i className="bi bi-house-door me-1"></i>
                        Exam Types
                    </button>
                </li>
                {selectedExamType && (
                    <li className="breadcrumb-item">
                        {selectedYear ? (
                            <button 
                                className="btn btn-link text-decoration-none p-0"
                                onClick={goBackToYears}
                            >
                                <i className={`bi ${selectedExamType.icon} me-1`}></i>
                                {selectedExamType.name}
                            </button>
                        ) : (
                            <span className={`text-${selectedExamType.color}`}>
                                <i className={`bi ${selectedExamType.icon} me-1`}></i>
                                {selectedExamType.name}
                            </span>
                        )}
                    </li>
                )}
                {selectedYear && (
                    <li className="breadcrumb-item active">
                        <i className="bi bi-calendar me-1"></i>
                        {selectedYear}
                    </li>
                )}
            </ol>
        </nav>
    );

    // Exam Types View
    const ExamTypesView = () => (
        <div className="row">
            {/* Search Section */}
            <div className="col-lg-12 mb-4">
                <div className="card border-0 shadow-sm">
                    <div className="card-body">
                        <div className="row align-items-center">
                            <div className="col-md-8">
                                <h5 className="card-title mb-3">Browse by Exam Type</h5>
                                <p className="text-muted mb-0">
                                    Select an exam type to access past questions and answers
                                </p>
                            </div>
                            <div className="col-md-4">
                                <div className="input-group">
                                    <span className="input-group-text bg-white border-end-0">
                                        <i className="bi bi-search text-muted"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search exam types..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Exam Types Grid */}
            {loading ? (
                <div className="col-12 text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading exam types...</p>
                </div>
            ) : examTypes.length === 0 ? (
                <div className="col-12 text-center py-5">
                    <i className="bi bi-folder-x display-1 text-muted"></i>
                    <h4 className="mt-3">No Exam Types Found</h4>
                    <p className="text-muted">No exam materials available yet</p>
                </div>
            ) : (
                examTypes
                    .filter(examType => 
                        examType.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        examType.fullName.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map(examType => (
                        <div key={examType.id} className="col-lg-3 col-md-6 mb-4">
                            <div 
                                className={`card h-100 border-0 shadow-sm hover-lift cursor-pointer border-top border-${examType.color} border-top-3`}
                                onClick={() => handleExamTypeClick(examType.id)}
                                style={{ transition: 'all 0.3s ease' }}
                            >
                                <div className="card-body">
                                    <div className="d-flex align-items-start mb-3">
                                        <div className={`bg-${examType.color} bg-opacity-10 rounded-3 p-3 me-3`}>
                                            <i className={`bi ${examType.icon} text-${examType.color} fs-4`}></i>
                                        </div>
                                        <div>
                                            <h5 className="card-title mb-1">{examType.name}</h5>
                                            <p className="text-muted small mb-0">{examType.fullName}</p>
                                        </div>
                                    </div>
                                    
                                    <p className="text-muted small mb-3">{examType.description}</p>
                                    
                                    <div className="mt-3">
                                        <div className="row">
                                            <div className="col-6">
                                                <div className="text-center p-2 bg-light rounded">
                                                    <div className="h4 mb-0 text-primary">{examType.yearCount}</div>
                                                    <small className="text-muted">Years</small>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="text-center p-2 bg-light rounded">
                                                    <div className="h4 mb-0 text-success">{examType.categoryCount}</div>
                                                    <small className="text-muted">Subjects</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Recent Years */}
                                    {examType.years.length > 0 && (
                                        <div className="mt-3">
                                            <p className="small text-muted mb-2">Recent years:</p>
                                            <div className="d-flex flex-wrap gap-1">
                                                {examType.years.slice(0, 3).map(year => (
                                                    <span key={year} className="badge bg-light text-dark border">
                                                        {year}
                                                    </span>
                                                ))}
                                                {examType.years.length > 3 && (
                                                    <span className="badge bg-secondary">
                                                        +{examType.years.length - 3} more
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Sample Subjects */}
                                    {examType.categories.length > 0 && (
                                        <div className="mt-3">
                                            <p className="small text-muted mb-2">Available subjects:</p>
                                            <div className="d-flex flex-wrap gap-1">
                                                {examType.categories.slice(0, 3).map(cat => (
                                                    <span key={cat.id} className="badge bg-light text-dark border" title={cat.title}>
                                                        {cat.title.length > 15 ? cat.title.substring(0, 15) + '...' : cat.title}
                                                    </span>
                                                ))}
                                                {examType.categories.length > 3 && (
                                                    <span className="badge bg-secondary">
                                                        +{examType.categories.length - 3} more
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="card-footer bg-transparent border-top">
                                    <button className={`btn btn-sm btn-${examType.color} w-100`}>
                                        {examType.categoryCount > 0 ? `View ${examType.yearCount} Years` : 'No Content'}
                                        <i className="bi bi-chevron-right ms-2"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
            )}
            
            {/* Show empty exam types with "Coming Soon" badge */}
            {!loading && examTypes.length > 0 && (
                Object.keys(examTypeConfig)
                    .filter(typeKey => !examTypes.find(t => t.id === typeKey))
                    .map(typeKey => {
                        const examType = examTypeConfig[typeKey];
                        return (
                            <div key={typeKey} className="col-lg-3 col-md-6 mb-4">
                                <div 
                                    className={`card h-100 border-0 shadow-sm border-top border-${examType.color} border-top-3 opacity-50`}
                                    style={{ transition: 'all 0.3s ease' }}
                                >
                                    <div className="card-body">
                                        <div className="d-flex align-items-start mb-3">
                                            <div className={`bg-${examType.color} bg-opacity-10 rounded-3 p-3 me-3`}>
                                                <i className={`bi ${examType.icon} text-${examType.color} fs-4`}></i>
                                            </div>
                                            <div>
                                                <h5 className="card-title mb-1">{examType.name}</h5>
                                                <p className="text-muted small mb-0">{examType.fullName}</p>
                                            </div>
                                        </div>
                                        
                                        <p className="text-muted small mb-3">{examType.description}</p>
                                        
                                        <div className="mt-3">
                                            <div className="row">
                                                <div className="col-6">
                                                    <div className="text-center p-2 bg-light rounded">
                                                        <div className="h4 mb-0 text-muted">0</div>
                                                        <small className="text-muted">Years</small>
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-center p-2 bg-light rounded">
                                                        <div className="h4 mb-0 text-muted">0</div>
                                                        <small className="text-muted">Subjects</small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="mt-3 text-center">
                                            <span className="badge bg-secondary">
                                                <i className="bi bi-clock me-1"></i>
                                                Coming Soon
                                            </span>
                                        </div>
                                    </div>
                                    <div className="card-footer bg-transparent border-top">
                                        <button className={`btn btn-sm btn-outline-${examType.color} w-100`} disabled>
                                            Coming Soon
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })
            )}
        </div>
    );

    // Years View
    const YearsView = () => {
        if (!selectedExamType) return null;
        
        const years = selectedExamType.years || [];
        
        return (
            <div className="row">
                <div className="col-lg-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-4">
                                <div className={`bg-${selectedExamType.color} bg-opacity-10 rounded-3 p-3 me-3`}>
                                    <i className={`bi ${selectedExamType.icon} text-${selectedExamType.color} fs-3`}></i>
                                </div>
                                <div>
                                    <h5 className="card-title mb-1">{selectedExamType.name} Past Questions</h5>
                                    <p className="text-muted mb-0">
                                        Select a year to download question papers and answer sheets
                                    </p>
                                </div>
                            </div>
                            
                            {loading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Loading years...</p>
                                </div>
                            ) : years.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-calendar-x display-1 text-muted"></i>
                                    <h4 className="mt-3">No Years Available</h4>
                                    <p className="text-muted">No exam years found for {selectedExamType.name}</p>
                                </div>
                            ) : (
                                <div className="row">
                                    {years.map(year => (
                                        <div key={year} className="col-xl-2 col-lg-3 col-md-4 col-sm-6 mb-3">
                                            <div 
                                                className={`card border-0 shadow-sm hover-lift cursor-pointer h-100 border-top border-${selectedExamType.color}`}
                                                onClick={() => handleYearClick(year)}
                                                style={{ transition: 'all 0.3s ease' }}
                                            >
                                                <div className="card-body text-center py-4">
                                                    <div className={`display-6 fw-bold text-${selectedExamType.color} mb-2`}>
                                                        {year}
                                                    </div>
                                                    <p className="text-muted small mb-0">
                                                        <i className="bi bi-file-earmark-pdf me-1"></i>
                                                        View Papers
                                                    </p>
                                                </div>
                                                <div className="card-footer bg-transparent border-top text-center">
                                                    <button className={`btn btn-sm btn-${selectedExamType.color}`}>
                                                        Select <i className="bi bi-chevron-right ms-1"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {/* Available Subjects */}
                            {selectedExamType.categories.length > 0 && (
                                <div className="mt-5">
                                    <h6 className="mb-3">Available Subjects ({selectedExamType.categories.length})</h6>
                                    <div className="row">
                                        {selectedExamType.categories.map(category => (
                                            <div key={category.id} className="col-md-3 col-sm-6 mb-2">
                                                <div className="d-flex align-items-center p-2 bg-light rounded">
                                                    <i className="bi bi-journal-text me-2 text-muted"></i>
                                                    <span className="small">{category.title}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Downloads View
    const DownloadsView = () => {
        if (!selectedExamType || !selectedYear) return null;
        
        // Group questions by category/subject
        const groupedQuestions = {};
        questions.forEach(question => {
            const category = question.category_name || "General";
            if (!groupedQuestions[category]) {
                groupedQuestions[category] = [];
            }
            groupedQuestions[category].push(question);
        });

        return (
            <div className="row">
                <div className="col-lg-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <div>
                                    <h5 className="card-title mb-1">
                                        <i className={`bi ${selectedExamType.icon} me-2 text-${selectedExamType.color}`}></i>
                                        {selectedExamType.name} - {selectedYear} Question Papers
                                    </h5>
                                    <p className="text-muted small mb-0">
                                        Download all available papers for {selectedYear}
                                    </p>
                                </div>
                                <div className="d-flex gap-2">
                                    <span className={`badge bg-${selectedExamType.color} px-3 py-2`}>
                                        {questions.length} Papers
                                    </span>
                                    <button 
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={goBackToYears}
                                    >
                                        <i className="bi bi-arrow-left me-1"></i>
                                        Back
                                    </button>
                                </div>
                            </div>

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3 text-muted">Loading papers...</p>
                                </div>
                            ) : questions.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-file-earmark-x display-1 text-muted"></i>
                                    <h4 className="mt-3">No Papers Available</h4>
                                    <p className="text-muted mb-4">
                                        No question papers found for {selectedExamType.name} {selectedYear}
                                    </p>
                                    <button 
                                        className="btn btn-outline-primary"
                                        onClick={goBackToYears}
                                    >
                                        <i className="bi bi-arrow-left me-1"></i>
                                        Back to Years
                                    </button>
                                </div>
                            ) : (
                                Object.keys(groupedQuestions).map(category => (
                                    <div key={category} className="mb-5">
                                        <h6 className="border-bottom pb-2 mb-3">
                                            <i className="bi bi-folder me-2"></i>
                                            {category}
                                        </h6>
                                        <div className="row">
                                            {groupedQuestions[category].map(question => (
                                                <div key={question.id} className="col-lg-6 mb-4">
                                                    <div className="card border-0 shadow-sm h-100">
                                                        <div className="card-body">
                                                            <h6 className="card-title text-truncate">
                                                                {question.title}
                                                            </h6>
                                                            
                                                            <div className="mb-3">
                                                                <p className="text-muted small mb-2">
                                                                    <i className="bi bi-calendar me-1"></i>
                                                                    Uploaded: {question.created_date}
                                                                </p>
                                                                {question.description && (
                                                                    <p className="small text-muted">
                                                                        {question.description}
                                                                    </p>
                                                                )}
                                                            </div>

                                                            {/* Download Buttons */}
                                                            <div className="row g-2">
                                                                <div className="col-6">
                                                                    <button
                                                                        className="btn btn-outline-danger w-100"
                                                                        onClick={() => handleDownload(question, "question")}
                                                                        disabled={!question.question_file}
                                                                    >
                                                                        <i className="bi bi-file-earmark-pdf me-1"></i>
                                                                        Questions
                                                                    </button>
                                                                </div>
                                                                <div className="col-6">
                                                                    <button
                                                                        className="btn btn-outline-success w-100"
                                                                        onClick={() => handleDownload(question, "answer")}
                                                                        disabled={!question.answer_file}
                                                                    >
                                                                        <i className="bi bi-file-earmark-check me-1"></i>
                                                                        Answers
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="card-footer bg-transparent border-top">
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <small className="text-muted">
                                                                    {question.upload_type === 'pdf' ? 'PDF Document' : 'Text Format'}
                                                                </small>
                                                                <button
                                                                    className="btn btn-sm btn-link text-decoration-none"
                                                                    onClick={() => handleDownload(question, "question")}
                                                                    disabled={!question.question_file}
                                                                >
                                                                    <i className="bi bi-download me-1"></i>
                                                                    Download All
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Bulk Download Section */}
                    {questions.length > 0 && (
                        <div className="card border-0 shadow-sm mt-4">
                            <div className="card-body">
                                <div className="row align-items-center">
                                    <div className="col-md-8">
                                        <h6 className="mb-2">
                                            <i className="bi bi-download me-2"></i>
                                            Download All {selectedYear} Papers
                                        </h6>
                                        <p className="text-muted small mb-0">
                                            Get all {questions.length} papers in one package
                                        </p>
                                    </div>
                                    <div className="col-md-4 text-md-end">
                                        <button className="btn btn-primary">
                                            <i className="bi bi-box-arrow-down me-2"></i>
                                            Download ZIP Package
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            <Navbar />
            
            <main className="main-content">
                {/* Hero Section */}
                <section className="hero" style={{
                    background: 'linear-gradient(135deg, #3b50ac 0%, #070707 100%)'
                }}>
                    <div className="container">
                        <div className="row align-items-center mt-5">
                            <div className="col-lg-8">
                                <h1 className="display-5 fw-bold text-white mb-3">
                                    Past Questions Repository
                                </h1>
                                <p className="lead text-white opacity-90 mb-4">
                                    Access comprehensive past exam papers from major examination bodies.
                                    Perfect for students preparing for important examinations.
                                </p>
                                <div className="d-flex flex-wrap gap-2 mb-2">
                                    <span className="badge bg-white text-primary px-3 py-2">
                                        <i className="bi bi-shield-check me-1"></i>
                                        Verified Content
                                    </span>
                                    <span className="badge bg-white text-primary px-3 py-2">
                                        <i className="bi bi-cloud-arrow-down me-1"></i>
                                        Free Downloads
                                    </span>
                                    <span className="badge bg-white text-primary px-3 py-2">
                                        <i className="bi bi-clock-history me-1"></i>
                                        Updated Regularly
                                    </span>
                                </div>
                            </div>
                            <div className="col-lg-4 text-lg-start">
                                <div className="bg-white rounded-3 p-4 shadow-lg">
                                    <h6 className="text-primary mb-3">
                                        <i className="bi bi-lightbulb me-2"></i>
                                        How to Use
                                    </h6>
                                    <ul className="list-unstyled small">
                                        <li className="mb-2">
                                            <span className="badge bg-primary me-2">1</span>
                                            Select your exam type
                                        </li>
                                        <li className="mb-2">
                                            <span className="badge bg-success me-2">2</span>
                                            Choose the exam year
                                        </li>
                                        <li className="mb-2">
                                            <span className="badge bg-warning me-2">3</span>
                                            Download question papers
                                        </li>
                                        <li>
                                            <span className="badge bg-info me-2">4</span>
                                            Practice with answers
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Main Content */}
                <section className="py-5">
                    <div className="container">
                        {/* Breadcrumb */}
                        <div className="mb-4">
                            <Breadcrumb />
                        </div>

                        {/* Content based on active tab */}
                        {activeTab === "examTypes" && <ExamTypesView />}
                        {activeTab === "years" && <YearsView />}
                        {activeTab === "downloads" && <DownloadsView />}
                    </div>
                </section>
            </main>

            <Footer />

            <style jsx>{`
                .hover-lift {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                
                .hover-lift:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15) !important;
                }
                
                .cursor-pointer {
                    cursor: pointer;
                }
                
                .main-content {
                    min-height: calc(100vh - 160px);
                }
                
                .breadcrumb {
                    background-color: rgba(255, 255, 255, 0.9);
                    backdrop-filter: blur(10px);
                }
                
                .card {
                    border-radius: 12px;
                    overflow: hidden;
                }
                
                @media (max-width: 768px) {
                    .hero-section .display-5 {
                        font-size: 2rem;
                    }
                    
                    .card-body {
                        padding: 1.25rem;
                    }
                }
                
                @media (max-width: 576px) {
                    .hero-section {
                        padding: 3rem 0;
                    }
                    
                    .breadcrumb {
                        padding: 0.75rem;
                        font-size: 0.875rem;
                    }
                    
                    .col-xl-2 {
                        flex: 0 0 50%;
                        max-width: 50%;
                    }
                }
            `}</style>
        </>
    );
};

export default Questions;